"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import MCMCombobox, { ComponentLink } from "@/components/ui/MCMCombobox";
import { Button } from "@/components/ui/button";
import { useTenancy } from "@/contexts/TenancyContext";
import { MCMService } from "@/services/mcmService";

export default function MCMComboboxExample() {
  const [links, setLinks] = React.useState<ComponentLink[]>([]);
  const [controlID, setControlID] = React.useState<string | null>(null);
  const { activeTenancy } = useTenancy();
  const [saving, setSaving] = React.useState(false);
  const busy = saving || !activeTenancy?.id || !controlID || links.length === 0;

  const applyToMCM = async () => {
    if (!activeTenancy?.id || !controlID || links.length === 0) return;
    setSaving(true);
    try {
      await MCMService.bulkUpdate({
        controlIds: [controlID],
        type: 'linkedComponents',
        action: 'add',
        data: { links }
      }, activeTenancy.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>MCM Combobox</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select items across categories (controls, clauses, assets, suppliers, risks, policies, legal obligations, objectives, assurance tasks, checklists, docs, privacy requests, data types, issues, interested parties, audits, findings, corrective actions).
          </p>
          <MCMCombobox 
            selected={links} 
            onChange={setLinks} 
            onChangePayload={({ controlID: cid }) => setControlID(cid)}
            placeholder="Select control and link items..." 
            fullWidth 
          />

          <div className="flex items-center gap-2">
            <Badge variant="secondary">Selected</Badge>
            <span className="text-sm text-muted-foreground">{links.length} item{links.length !== 1 ? "s" : ""}</span>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">ComponentLink payload</div>
            <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-[320px]">
{JSON.stringify({ controlID, linkedComponents: links }, null, 2)}
            </pre>
          </div>

          <div className="flex justify-end">
            <Button disabled={busy} onClick={applyToMCM}>
              {saving ? 'Saving...' : 'Apply to MCM'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
